import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  Paper,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Snackbar
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import QrCodeIcon from '@mui/icons-material/QrCode'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { visuallyHidden } from '@mui/utils'

import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/fa'
import { faIR } from '@mui/x-date-pickers/locales'
import QRCode from 'react-qr-code'

type Order = 'asc' | 'desc'
type SortableColumn = 'name' | 'start_at'

interface Plan {
  id: number
  name: string
  title: string
  min_price: number | string
  price?: number | string
  ratio: number
  durations: { duration: number }[]
  isActive: boolean
  plan_option_id?: number
}

interface TradeAccount {
  id: number
  account_number: string
  account_password?: string
  is_active: boolean
  plan: {
    id: number
    title: string
    start_at: number | null
    end_at: number | null
    price?: number | string // قیمت نهایی پلن
    min_price?: number | string
    final_price?: number | string
  } | null
  user?: {
    first_name: string
    last_name: string
    email: string
  }
  account_type: 'personal' | 'prop'

  price?: number | string
  is_full_discount?: boolean | string
  has_full_discount?: boolean | string
  is_free?: boolean | string
  free?: boolean
  discount?: { percent?: number | string; amount?: number | string }
  applied_discount?: { percent?: number | string }
  discount_percent?: number | string
  meta?: any
}

interface TradeAccountsTabProps {
  apiFetch: (url: string, options?: RequestInit) => Promise<any>
  plans: Plan[]
  setViewSettingsOpen: (open: boolean) => void
  setSettingsToView: (settings: any) => void
  setOptionsForView: (options: any) => void
  setViewLoading: (loading: boolean) => void
  setActivePlanName: (name: string) => void
  setError: (error: string | null) => void
  theme?: string
}

const formatTimestamp = (timestamp: number | null) => {
  if (!timestamp) return '---'
  const date = new Date(timestamp * 1000)
  return date
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    .replace(/\//g, '/')
}

function descendingComparator(a: TradeAccount, b: TradeAccount, orderBy: SortableColumn) {
  if (orderBy === 'start_at') {
    const aValue = a.plan?.start_at || 0
    const bValue = b.plan?.start_at || 0

    if (bValue < aValue) return -1
    if (bValue > aValue) return 1
    return 0
  }

  if (orderBy === 'name') {
    const aName = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim()
    const bName = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim()
    return bName.localeCompare(aName, 'fa', { sensitivity: 'base' })
  }

  return 0
}

function getComparator(order: Order, orderBy: SortableColumn): (a: TradeAccount, b: TradeAccount) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function sortData(array: TradeAccount[], comparator: (a: TradeAccount, b: TradeAccount) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [TradeAccount, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

const TradeAccountsTab: React.FC<TradeAccountsTabProps> = ({
  apiFetch,
  plans,
  setViewSettingsOpen,
  setSettingsToView,
  setOptionsForView,
  setViewLoading,
  setActivePlanName,
  setError,
  theme
}) => {
  const [tradeAccounts, setTradeAccounts] = useState<TradeAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [internalError, setInternalError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(true)

  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<SortableColumn>('start_at')

  const [editDateModalOpen, setEditDateModalOpen] = useState(false)

  const [editingAccountData, setEditingAccountData] = useState<{
    id: number
    accountName: string
    startAt: dayjs.Dayjs | null
    endAt: dayjs.Dayjs | null
  } | null>(null)

  const [saveDateLoading, setSaveDateLoading] = useState(false)
  const [qrDialogOpen, setQrDialogOpen] = useState(false)
  const [qrValue, setQrValue] = useState<string | null>(null)
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false)

  const fetchTradeAccounts = useCallback(async () => {
    setLoading(true)
    setInternalError(null)
    try {
      const data = await apiFetch('/admin/trade-account')
      const accounts = data.items || data
      const accountsArray = Array.isArray(accounts) ? accounts : []
      setTradeAccounts(accountsArray)
    } catch (err: any) {
      setInternalError(`خطا در بارگذاری حساب‌های تجاری: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }, [apiFetch])

  useEffect(() => {
    fetchTradeAccounts()
  }, [fetchTradeAccounts])

  // const checkIsFullDiscount = (acc: any) => {
  //   if (!acc) return false;

  //   if (acc.price !== undefined && Number(acc.price) === 0) return true;

  //   if (acc.plan) {
  //     const p = acc.plan;
  //     if (p.final_price !== undefined && Number(p.final_price) === 0) return true;
  //     if (p.price !== undefined && Number(p.price) === 0) return true;
  //   }

  //   return false;
  // };

  const handleRequestSort = (property: SortableColumn) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleCopyPassword = async (password?: string) => {
    if (!password) return
    try {
      await navigator.clipboard.writeText(password)
      setCopySnackbarOpen(true)
    } catch (e) {
      console.error('Copy failed', e)
    }
  }

  const handleOpenQr = (value?: string) => {
    if (!value) return
    setQrValue(value)
    setQrDialogOpen(true)
  }

  const handleCloseQr = () => {
    setQrDialogOpen(false)
    setQrValue(null)
  }

  const handleCloseSnackbar = () => {
    setCopySnackbarOpen(false)
  }

  const handleToggleAccountStatus = async (accountId: number, currentStatus: boolean) => {
    const newStatus = !currentStatus
    setTradeAccounts(prev => prev.map(acc => (acc.id === accountId ? { ...acc, is_active: newStatus } : acc)))
    try {
      await apiFetch(`/trade-account/${accountId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: newStatus })
      })
    } catch (err: any) {
      setInternalError(`خطا در به‌روزرسانی وضعیت حساب: ${err.message}`)
      setTradeAccounts(prev => prev.map(acc => (acc.id === accountId ? { ...acc, is_active: currentStatus } : acc)))
    }
  }

  const handleViewSettingsClick = async (account: TradeAccount) => {
    if (!account.plan) return

    setViewLoading(true)
    setViewSettingsOpen(true)
    setSettingsToView(null)
    setOptionsForView(null)
    setActivePlanName(account.plan.title)
    setError(null)

    // First try to use settings included in the trade-account payload
    const localSettings =
      // common possible locations for plan settings returned with the account
      (account as any).plan_settings ??
      (account as any).plan?.plan_settings ??
      (account as any).meta?.plan_settings ??
      (account as any).settings ??
      (account as any).plan?.settings

    if (localSettings !== undefined && localSettings !== null) {
      setSettingsToView(localSettings)
    } else {
      // Fallback to calling the settings API only if settings not present on the account
      try {
        const savedSettingsData = await apiFetch(`/trade-account/${account.id}/settings`)
        setSettingsToView(savedSettingsData.plan_settings)
      } catch (e: any) {
        if (e.message.includes('plan settings not found') || e.message.includes('Plan settings not configured')) {
          setSettingsToView({ notConfigured: true })
        } else {
          const errorMessage = `خطا در دریافت تنظیمات: ${e.message}`
          setSettingsToView({ error: errorMessage })
        }
      }
    }

    try {
      const planData = plans.find(p => p.id === account.plan?.id)
      if (planData && planData.plan_option_id) {
        const optionsData = await apiFetch(`/plan/options/${planData.plan_option_id}`)
        setOptionsForView(optionsData.data || {})
      } else {
        setOptionsForView({})
      }
    } catch (e: any) {
      console.error('Error fetching plan options for view', e)
      setOptionsForView({})
    } finally {
      setViewLoading(false)
    }
  }

  const handleEditDatesClick = (account: TradeAccount) => {
    if (!account.plan) return

    const userName = `${account.user?.first_name || ''} ${account.user?.last_name || ''}`
    setEditingAccountData({
      id: account.id,
      accountName: userName.trim() || account.account_number,
      startAt: account.plan.start_at ? dayjs.unix(account.plan.start_at) : null,
      endAt: account.plan.end_at ? dayjs.unix(account.plan.end_at) : null
    })
    setEditDateModalOpen(true)
  }

  const handleCloseEditDateModal = () => {
    setEditDateModalOpen(false)
    setEditingAccountData(null)
  }

  const handleSaveDates = async () => {
    if (!editingAccountData) return

    setSaveDateLoading(true)
    try {
      const payload = {
        start_at: editingAccountData.startAt ? editingAccountData.startAt.unix() : null,
        end_at: editingAccountData.endAt ? editingAccountData.endAt.unix() : null
      }

      await apiFetch(`/admin/trade-account/${editingAccountData.id}/plan`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      })

      setTradeAccounts(prev =>
        prev.map(acc => {
          if (acc.id === editingAccountData.id && acc.plan) {
            return {
              ...acc,
              plan: {
                ...acc.plan,
                start_at: payload.start_at,
                end_at: payload.end_at
              }
            }
          }
          return acc
        })
      )

      handleCloseEditDateModal()
    } catch (err: any) {
      setError(`خطا در ویرایش تاریخ‌ها: ${err.message}`)
    } finally {
      setSaveDateLoading(false)
    }
  }

  const formInputStyles = {
    '& .MuiInputBase-root': {
      height: '56px',
      backgroundColor: 'var(--input-background)',
      borderRadius: '8px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiInputBase-input': {
      textAlign: 'center',
      color: 'var(--text-color)'
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (internalError) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        {internalError}
      </Alert>
    )
  }

  const collapsibleHeaderSx = {
    p: 2,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }

  const tableHeadCellSx = {
    backgroundColor: 'var(--input-background)',
    color: 'var(--text-color)',
    fontWeight: 'bold',
    borderBottom: '1px solid var(--border-color)',
    whiteSpace: 'nowrap',
    padding: '8px 16px'
  }

  const sortedAccounts = sortData(tradeAccounts, getComparator(order, orderBy))

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale='fa'
      localeText={faIR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Paper className='management-card' sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={collapsibleHeaderSx} onClick={() => setIsOpen(prev => !prev)}>
          <h3 className='tab-section-header' style={{ border: 'none', margin: 0, padding: 0 }}>
            مدیریت حساب‌های تجاری
          </h3>
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Box>
        <Collapse in={isOpen}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            {tradeAccounts.length > 0 ? (
              <TableContainer component={Box} sx={{ maxHeight: 600 }}>
                <Table stickyHeader aria-label='trade accounts table' dir='rtl'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx} sortDirection={orderBy === 'name' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => handleRequestSort('name')}
                        >
                          نام کاربر
                          {orderBy === 'name' ? (
                            <Box component='span' sx={visuallyHidden}>
                              {order === 'desc' ? 'مرتب شده نزولی' : 'مرتب شده صعودی'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={tableHeadCellSx}>ایمیل</TableCell>
                      <TableCell align='center' sx={tableHeadCellSx}>
                        شماره حساب
                      </TableCell>
                      <TableCell align='center' sx={tableHeadCellSx}>
                        رمز حساب
                      </TableCell>
                      <TableCell align='center' sx={tableHeadCellSx}>
                        پلن
                      </TableCell>
                      {/*  <TableCell align='center' sx={tableHeadCellSx}>
                        دمو
                      </TableCell> */}
                      <TableCell align='center' sx={tableHeadCellSx}>
                        نوع حساب
                      </TableCell>

                      <TableCell
                        align='center'
                        sx={tableHeadCellSx}
                        sortDirection={orderBy === 'start_at' ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === 'start_at'}
                          direction={orderBy === 'start_at' ? order : 'asc'}
                          onClick={() => handleRequestSort('start_at')}
                        >
                          تاریخ شروع / پایان
                          {orderBy === 'start_at' ? (
                            <Box component='span' sx={visuallyHidden}>
                              {order === 'desc' ? 'مرتب شده نزولی' : 'مرتب شده صعودی'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell align='center' sx={tableHeadCellSx}>
                        تنظیمات
                      </TableCell>
                      <TableCell align='center' sx={tableHeadCellSx}>
                        وضعیت
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAccounts.map(account => (
                      <TableRow
                        key={account.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' }
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {account.user?.first_name || ''} {account.user?.last_name || ''}
                        </TableCell>
                        <TableCell>{account.user?.email || '---'}</TableCell>
                        <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: '1.1em' }}>
                          {account.account_number}
                        </TableCell>
                        <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: '1.1em', color: 'text.secondary' }}>
                          {account.account_password ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Tooltip title='کپی رمز'>
                                <IconButton size='small' onClick={() => handleCopyPassword(account.account_password)}>
                                  <ContentCopyIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='نمایش QR رمز حساب'>
                                <IconButton size='small' onClick={() => handleOpenQr(account.account_password)}>
                                  <QrCodeIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            '---'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {account.plan?.title || (
                            <Typography variant='caption' color='text.secondary'>
                              تعیین نشده
                            </Typography>
                          )}
                        </TableCell>

                        {/* نمایش تخفیف */}
                        {/*  <TableCell align='center'>
                          {checkIsFullDiscount(account) ? (
                            <Chip label='✅' color='success' size='small' />
                          ) : (
                            <Typography variant='caption' color='text.secondary'>
                            ❌
                            </Typography>
                          )}
                        </TableCell> */}

                        <TableCell align='center'>{account.account_type === 'prop' ? 'پراپ' : 'شخصی'}</TableCell>
                        <TableCell align='center'>
                          {account.plan ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
                                <Typography variant='caption' display='block'>
                                  شروع: {formatTimestamp(account.plan.start_at)}
                                </Typography>
                                <Typography variant='caption' display='block' color='text.secondary'>
                                  انقضا: {formatTimestamp(account.plan.end_at)}
                                </Typography>
                              </Box>
                              <Tooltip title='ویرایش تاریخ‌ها'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleEditDatesClick(account)}
                                  sx={{ ml: 1, color: 'var(--text-muted-color)' }}
                                >
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            '---'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {account.plan ? (
                            <Tooltip title='مشاهده تنظیمات'>
                              <IconButton color='primary' onClick={() => handleViewSettingsClick(account)} size='small'>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant='caption' color='text.secondary'>
                              ---
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <Switch
                            checked={account.is_active}
                            onChange={() => handleToggleAccountStatus(account.id, account.is_active)}
                            color='primary'
                            size='small'
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box p={3} textAlign='center'>
                <Typography>هیچ حساب تجاری برای نمایش وجود ندارد.</Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Dialog
        open={editDateModalOpen}
        onClose={handleCloseEditDateModal}
        PaperProps={{
          sx: {
            bgcolor: 'var(--background-color-secondary)',
            color: 'var(--text-color)',
            backgroundImage: 'none',
            minWidth: '350px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid var(--border-color)' }}>
          ویرایش مدت زمان پلن
          {editingAccountData && (
            <Typography variant='subtitle2' color='var(--text-color)' sx={{ mt: 1 }}>
              برای کاربر: {editingAccountData.accountName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, overflowY: 'visible' }}>
          {' '}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <label>تاریخ شروع</label>
              <DateTimePicker
                value={editingAccountData?.startAt || null}
                onChange={newValue => setEditingAccountData(prev => (prev ? { ...prev, startAt: newValue } : null))}
                renderInput={params => <TextField {...params} fullWidth sx={formInputStyles} />}
                DialogProps={{ PaperProps: { className: `time-picker-dialog ${theme || ''}` } }}
              />
            </Grid>
            <Grid item xs={12}>
              <label>تاریخ پایان</label>
              <DateTimePicker
                value={editingAccountData?.endAt || null}
                onChange={newValue => setEditingAccountData(prev => (prev ? { ...prev, endAt: newValue } : null))}
                renderInput={params => <TextField {...params} fullWidth sx={formInputStyles} />}
                DialogProps={{ PaperProps: { className: `time-picker-dialog ${theme || ''}` } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--border-color)' }}>
          <Button onClick={handleCloseEditDateModal} color='inherit'>
            انصراف
          </Button>
          <Button onClick={handleSaveDates} variant='contained' color='primary' disabled={saveDateLoading}>
            {saveDateLoading ? <CircularProgress size={24} color='inherit' /> : 'ذخیره تغییرات'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={qrDialogOpen} onClose={handleCloseQr} PaperProps={{ sx: { p: 3, textAlign: 'center' } }}>
        <DialogTitle>QR رمز حساب</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {qrValue ? (
            <QRCode value={qrValue} size={200} />
          ) : (
            <Typography color='text.secondary'>مقادیر QR در دسترس نیست</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => { if (qrValue) handleCopyPassword(qrValue) }} startIcon={<ContentCopyIcon  sx={{mx:2}}/>}>کپی رمز</Button>
          <Button onClick={handleCloseQr} color='inherit'>بستن</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={copySnackbarOpen} autoHideDuration={2500} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity='success' sx={{ width: '100%' }}>
          رمز در کلیپ‌بورد کپی شد
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  )
}

export default TradeAccountsTab
